// Import Libraries
import React, { useRef, useEffect, useState } from "react";
import PageSEO from "../../../../Components/SEO/PageSEO";
import { useLocation } from 'react-router-dom';
import { getProducts } from "../../../../ApiLayer/apiLayer";

// Import Components
import FilterComponent from "../../../../Components/FilterComponent/FilterComponent";
import ServerStandardsComponent from "../../../../Components/ServerStandardsComponent/ServerStandardsComponent";
import DedicatedServerLocations from "./Components/DedicatedServerLocations/DedicatedServerLocations";
import TestNetworkSpeedsComponent from "./Components/TestNetworkSpeedsComponent/TestNetworkSpeedsComponent";
import ServerPageIntro from "../../../../Components/ServerPageIntro/ServerPageIntro";

// Import Assets
import StandardServer from "../../../../Assets/Standard_Server.png";

// Import Animation
import FadeInOnScroll from "../../../../util/Animations/FadeInOnScroll";

const DedicatedServersPage = () => {
  const filterRef = useRef(null);
  const location = useLocation();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getProducts();
        if (response.products) {
          // Filter for standard servers only
          const standardServers = response.products.filter(product => 
            product.customdata?.type?.toLowerCase().includes('standard')
          );
          setProducts(standardServers);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (location.hash === "#filterComponent" && filterRef.current) {
      filterRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  // Define structured data for the page
  const standardServersSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "WebNX Standard Dedicated Servers",
    "description": "High-performance standard dedicated servers with instant provisioning, featuring premium Intel and AMD processors, ECC memory, and 10Gbps network connectivity.",
    "brand": {
      "@type": "Brand",
      "name": "WebNX"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "offers": products.map(product => ({
        "@type": "Offer",
        "name": `${product.customdata?.processor || 'Standard Server'}`,
        "description": `${product.customdata?.processor || ''} with ${product.customdata?.memory || ''}, ${product.customdata?.storage || ''}, ${product.customdata?.network || ''}`,
        "price": product.pricing?.USD?.monthly || "",
        "priceCurrency": "USD",
        "availability": product.stockcontrol === "true" && product.stocklevel > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "url": product.product_url || "",
        "itemCondition": "https://schema.org/NewCondition",
        "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "seller": {
          "@type": "Organization",
          "name": "WebNX"
        }
      }))
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Server Type",
        "value": "Standard Dedicated"
      },
      {
        "@type": "PropertyValue",
        "name": "Deployment",
        "value": "Instant Provisioning"
      },
      {
        "@type": "PropertyValue",
        "name": "Network",
        "value": "10Gbps with DDoS Protection"
      },
      {
        "@type": "PropertyValue",
        "name": "Support",
        "value": "24/7 Technical Support"
      }
    ]
  };

  return (
    <div>
      <PageSEO
        title="Standard Dedicated Servers | Instant Provision High-Performance Servers"
        description="Get instant access to high-performance dedicated servers with WebNX. Featuring premium Intel and AMD processors, ECC memory, 10Gbps network, and 24/7 support. Instant provisioning available."
        keywords="standard dedicated servers, instant provision servers, high-performance servers, enterprise servers, WebNX hosting, dedicated hosting, bare metal servers, instant deployment, server hosting"
        path="/standard-dedicated-servers"
        image="https://webnx.com/assets/dedicated-servers.jpg"
        schema={standardServersSchema}
        socialProfiles={{
          facebook: "https://www.facebook.com/WebNX/",
          linkedin: "https://www.linkedin.com/company/webnx/"
        }}
      />

      <FadeInOnScroll>
        <ServerPageIntro
          header="WebNX Dedicated Servers"
          subheader="High-Performance Bare Metal Solutions"
          introText="Experience unmatched performance with WebNX's custom-built dedicated servers. We offer a comprehensive range of configurations to suit any need, from single to quad CPU servers. With premium hardware from industry leaders like SuperMicro, Intel, and AMD, combined with our High-Performance BGP Route Optimized networks, you get the perfect balance of power and reliability."
          image={StandardServer}
          hideImageOnMobile={true}
          features={[
            {
              icon: "FaServer",
              title: "Enterprise Hardware",
              description: "Premium Intel and AMD processors with ECC memory for maximum reliability"
            },
            {
              icon: "FaNetworkWired",
              title: "High-Speed Network",
              description: "High end Cisco and Arista core routers with terabits of upstream capacity to dozens of carriers and peering point, route optimized with Noction IRP."
            },
            {
              icon: "FaShieldAlt",
              title: "Security First",
              description: "Advanced firewall, IPMI access, and 24/7 security monitoring"
            },
            {
              icon: "FaClock",
              title: "Rapid Deployment",
              description: "Same-day setup with custom OS installation and configuration"
            }
          ]}
        />
      </FadeInOnScroll>

      <div id="filterComponent" ref={filterRef}>
        <FilterComponent defaultFilters={{ "Server Type": ["Standard"] }} />
      </div>

      <FadeInOnScroll>
        <ServerStandardsComponent />
      </FadeInOnScroll>

      <FadeInOnScroll>
        <DedicatedServerLocations />
      </FadeInOnScroll>

      <FadeInOnScroll>
        <TestNetworkSpeedsComponent />
      </FadeInOnScroll>
    </div>
  );
};

export default DedicatedServersPage;
